import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Github, Linkedin, Twitter, Globe, ChevronDown, ArrowRight } from 'lucide-react';
import { PROFILE_DATA } from '../../data/config';
import MagneticButton from '../MagneticButton';

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const socialLinks = [
    { icon: Github, url: PROFILE_DATA.social.github, label: 'GitHub' },
    { icon: Linkedin, url: PROFILE_DATA.social.linkedin, label: 'LinkedIn' },
    { icon: Twitter, url: PROFILE_DATA.social.twitter, label: 'Twitter' },
    { icon: Globe, url: PROFILE_DATA.social.website, label: 'Website' },
  ];

  const scrollToNext = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="flex items-center justify-center relative"
      style={{ minHeight: 'max(100vh, 100dvh)' }}
    >
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 text-center px-4 sm:px-6 w-full mx-auto py-20"
      >
        {/* Eyebrow */}
        <motion.p
          className="text-[0.7rem] uppercase tracking-[0.4em] text-coffee-300 mb-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          {PROFILE_DATA.location?.city ? `${PROFILE_DATA.location.city} · ` : ''}Available for work
        </motion.p>

        {/* Name — fluid size, single line, never wraps */}
        <motion.h1
          className="hero-title font-bold mb-6 leading-[0.95] whitespace-nowrap"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.04 }}
          style={{
            fontFamily: 'Righteous, sans-serif',
            fontSize: 'clamp(2.25rem, 9.5vw, 9rem)',
            background: 'linear-gradient(160deg, #fff5e0 0%, #ffdaa8 45%, #c9a875 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.035em',
          }}
        >
          {PROFILE_DATA.name}
        </motion.h1>

        {/* Title */}
        <motion.h2
          className="text-2xl md:text-4xl font-semibold gradient-text mb-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE, delay: 0.1 }}
        >
          {PROFILE_DATA.title}
        </motion.h2>

        {/* Tagline */}
        <motion.p
          className="text-lg md:text-xl text-cream-200/85 mb-14 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE, delay: 0.16 }}
        >
          {PROFILE_DATA.tagline}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-20"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE, delay: 0.22 }}
        >
          <MagneticButton href="#contact" className="btn-primary group min-w-[200px] gap-2">
            Get In Touch
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </MagneticButton>
          <MagneticButton href="#projects" className="btn-secondary min-w-[200px]">
            View My Work
          </MagneticButton>
        </motion.div>

        {/* Social Links */}
        <motion.div
          className="flex items-center justify-center gap-2 mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.32 }}
        >
          {socialLinks.map((link) => (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-cream-100/5 border border-cream-100/12 hover:border-caramel-400/40 hover:bg-cream-100/10 hover:-translate-y-0.5 transition-all duration-200"
              aria-label={link.label}
            >
              <link.icon className="w-4 h-4 text-cream-200" />
            </a>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToNext}
          className="cursor-pointer hover:opacity-100 inline-flex flex-col items-center gap-1.5 opacity-50 hover:opacity-80 transition-opacity"
          aria-label="Scroll to next section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 0.5 }}
        >
          <span className="text-[0.6rem] uppercase tracking-[0.4em] text-cream-300">
            Scroll
          </span>
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown className="w-5 h-5 text-cream-200" />
          </motion.span>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default Hero;
